import { colors, weights } from '@components/theme/styles/index';
import TagsDropdown from '@gen2/app/components/tags-dropdown/tags-dropdown';
import { Alert, Box, Button, styled } from '@mui/material';
import {
  DatePickerLineNew,
  EditableInputNew,
  TagsLine,
} from '@nx-fe/components';
import { rem } from 'polished';

interface IsEditSendInviteProps {
  $isEditSendInvite?: boolean;
  $opaque: boolean;
}

export const StyledSendInvites = styled('div')(
  ({ theme }) =>
    ({
      [theme.breakpoints.up('sm')]: {
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        height: 'calc(100vh - var(--header-height))',
        overflowX: 'hidden',
      },
    }) as any,
);

export const StyledSendInvitesBody = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

export const Stickyhead = styled('div')<IsEditSendInviteProps>(
  ({ $isEditSendInvite, theme }) => ({
    position: 'relative',
    minHeight: !$isEditSendInvite ? rem(53) : rem(69),

    [theme.breakpoints.down('lg')]: {
      height: $isEditSendInvite ? rem(90) : rem(53),
    },
    [theme.breakpoints.down('sm')]: {
      height: $isEditSendInvite ? rem(166) : rem(53),
    },
  }),
);

export const StyledActions = styled('div')<IsEditSendInviteProps>(
  ({ theme, $opaque }) => ({
    position: 'fixed',
    left: 0,
    background: colors.white,
    zIndex: 11,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    borderBottom: `1px solid ${colors.grayLight1}`,
    paddingTop: rem(8),
    paddingBottom: rem(8),
    paddingLeft: rem(96),
    paddingRight: rem(20),
    flexWrap: 'wrap',
    ...($opaque && {
      opacity: 0.5,
      pointerEvents: 'none',
      userSelect: 'none',
    }),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: rem(20),
      paddingRight: rem(20),
    },
  }),
);

export const AlertContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: rem(20),
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

export const StyledAlert = styled(Alert)({
  flex: 1,
  justifyContent: 'center',
  textAlign: 'center',
  width: '100%',
});

export const StyledAlertButton = styled(Button)({
  padding: `${rem(8)} ${rem(24)}`,
  height: rem(36),
  fontSize: rem(14),
});

export const StyledHead = styled('div')<{ $opaque: boolean }>(
  ({ $opaque, theme }) => ({
    ...($opaque && {
      opacity: 0.5,
      pointerEvents: 'none',
      userSelect: 'none',
    }),
    display: 'flex',
    flexDirection: 'row',
    borderBottom: `1px solid ${colors.grayLight1}`,
    [theme.breakpoints.down('1625')]: {
      flexDirection: 'column',
    },
  }),
);

interface FlexBoxProps {
  $expandFullWidth?: boolean;
}

export const FlexBox = styled('div')<FlexBoxProps>(({ $expandFullWidth }) => ({
  flex: `1 1 auto`,
  borderRight: $expandFullWidth ? 'none' : `1px solid ${colors.grayLight1}`,
}));

export const RightContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
  gap: rem(20),
  alignItems: 'center',
  padding: `${rem(30)} ${rem(20)} `,
  minWidth: rem(467),
  '.watchers-row': {
    width: '100%',
    padding: rem(10),
  },
  [theme.breakpoints.down('1625')]: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: rem(20),
    paddingLeft: `${rem(50)} `,
    '.watchers-row': {
      width: 'unset',
    },
  },
}));

export const StyledSendTo = styled(TagsLine)({
  width: '100%',
  minHeight: rem(45),
  borderBottom: `1px solid ${colors.grayLight1}`,
  flewGrow: 0,
});

export const StyledSubjectLine = styled(EditableInputNew)(({ theme }) => ({
  width: '100%',
  border: 'none',
  height: rem(80),
  paddingLeft: rem(16),
  paddingRight: rem(16),
  paddingTop: rem(12),
  paddingBottom: rem(12),
  borderBottom: `1px solid ${colors.grayLight1}`,
  [theme.breakpoints.up('sm')]: {
    paddingLeft: rem(50),
    paddingRight: rem(50),
  },
}));

export const SettingsFrame = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: 'inherit',
  flexWrap: 'wrap',
  [theme.breakpoints.down('1625')]: {
    borderBottom: `1px solid ${colors.grayLight1}`,
  },
}));

export const DateFrame = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'row',
  width: 'inherit',
  borderRight: `1px solid ${colors.grayLight1}`,
  [theme.breakpoints.down('1181')]: {
    borderRight: 'none',
    borderBottom: `1px solid ${colors.grayLight1}`,
  },
}));

export const StyledPointOfContact = styled(TagsDropdown)({
  width: '50%',
  minHeight: rem(45),
  height: rem(114),
});

export const Styledspan = styled('li')({
  maxWidth: '90%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const StyledDueDate = styled(DatePickerLineNew)({
  width: '50%',
  height: rem(114),
  border: 'none',
});

export const StyledReminder = styled('div')(({ theme }) => ({
  alignSelf: 'center',
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  justifyContent: 'flex-start',
  height: rem(114),
  gap: rem(16),
  color: colors.grayDark2,
  fontWeight: weights.medium,
  paddingLeft: rem(50),
  paddingRight: rem(50),
  [theme.breakpoints.up('xl')]: {
    justifyContent: 'center',
    paddingLeft: rem(0),
    paddingRight: rem(0),
  },
}));

export const StyledLoadTemplateBox = styled('div')({
  marginLeft: rem(45),
});

export const StyleStack = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  width: '100%',
  gap: rem(8),
});
